import { useCallback, useEffect, useRef, useState } from "react";
import PaymentService from "../../../../service/payment-service";
import {
  openEcpayWindow,
  submitToEcpay,
} from "../../../../utils/ecpay-redirect";
import InvoiceSheet from "../../../dialog/invoice-sheet/invoice-sheet";
import useAuth from "../../../../hooks/useAuth";
import { useLoginGate } from "../../../../context/login-gate";
import "./fuyaozi-ticket-style.scss";
import { LINE_URL } from "../../../../constant/contact";
import { track, EVENTS } from "../../../../utils/meta-pixel";

//這一頁賣的券別（對應 config/product-plans 的 type 與 user.tickets 的欄位）。
//
//  只賣問卦券一種，所以沒有分頁。
//  （白夜的問事券在 pages/baiye-question/ticket；合盤券不做 ——
//    緋紅還沒上架，見 config/product-plans 的說明。）
const TICKET_TYPE = "gua";

//—— 等券入帳的輪詢 ——
//  宣告在模組層而不是元件裡：寫在元件內的話每次 render 都是新的值，
//  useCallback 會把它們當成依賴而每次重建（eslint 也會直接擋下來）。
//
//  3 秒一次：刷完卡大概幾秒內後端就收到通知、券也發了。
//  更密會變成對自己的 API 灌請求（全域限流是 15 分鐘 200 次）。
const POLL_MS = 3000;
//最多等十分鐘。ATM／超商是幾小時甚至隔天才繳費的，
//  那種情況不該讓頁面無限期打 API —— 他下次進來自然會看到。
const POLL_MAX_MS = 10 * 60 * 1000;

//問卦 — 購買問卦券
//
//  方案是「選一個再結帳」而不是每張卡各有一顆購買鍵：
//  底部固定一顆結帳鈕，金額跟著選擇變動，使用者隨時看得到要付多少。
//
//  方案與餘額都從後端來。價格的唯一真相在後端 —— 結帳時只送 productID，
//  金額由後端查表決定，前端送什麼金額都不會被採用。
//  onBought  付款完成、券真的入帳之後通知外層（頁面會切回問卦）
const FuyaoziTicket = ({ onBought }) => {
  //  —— 抵達報價點 ——
  //  這支元件只有切到購券分頁時才會被渲染，所以掛載就等於「他看到價格了」。
  //  跟報告類的「抵達故事頁」是同一個事件、同一個深度 ——
  //  三條漏斗長度不同，用同一個事件才能讓廣告組公平比較（見 utils/meta-pixel）。
  useEffect(() => {
    //  代號要跟產品頁、結帳、CAPI 用同一套（constant/products 的 code），
    //  不然 Meta 會把購券分頁當成另一個不相干的內容。
    track(EVENTS.REACHED_OFFER, {
      content_ids: ["liuyao-fuyaozi"],
      content_name: "liuyao-fuyaozi",
    });
  }, []);

  const [plans, setPlans] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [tickets, setTickets] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  //送出結帳後到跳轉綠界之間有幾百毫秒，要鎖住按鈕避免重複建單
  const [submitting, setSubmitting] = useState(false);
  //發票選擇：電子發票是法規要求，而綠界付完款不會再問一次，
  //所以要在跳轉之前把載具／捐贈／統編收齊
  const [invoiceOpen, setInvoiceOpen] = useState(false);

  const { isLoggedIn } = useAuth();
  const { requireLogin } = useLoginGate();

  //—— 等付款完成的輪詢 ——
  //
  //  綠界開在新分頁（見下面 handleCheckout 的說明），所以這一頁還活著。
  //  付款結果是 server-to-server 送到後端的，瀏覽器這邊只能問「券進來了沒」。
  const pollRef = useRef(null);
  //  輪詢的比較基準：按下結帳那一刻的張數。
  //  用 ref 不用 state —— 它只給計時器裡的閉包讀，不需要觸發重畫。
  const beforeRef = useRef(0);

  const loadTickets = useCallback(
    () =>
      PaymentService.tickets()
        .then((res) => {
          const t = res.data?.data?.tickets;
          setTickets(t);
          return t;
        })
        //取不到就不顯示，不要讓整頁壞掉
        .catch(() => {
          setTickets(null);
          return null;
        }),
    [],
  );

  //餘額只在進頁時取一次。
  //  沒登入就不查 —— 這支端點要帶 token，訪客打會拿到 401，
  //  徒增一次失敗的請求（而且 console 會冒紅字，看起來像壞了）。
  useEffect(() => {
    if (isLoggedIn) loadTickets();
  }, [isLoggedIn, loadTickets]);

  //離開這一頁就停掉輪詢
  useEffect(() => () => clearInterval(pollRef.current), []);

  //方案只取一次（只有一種券了，沒有分頁可切）
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");

    PaymentService.plans(TICKET_TYPE)
      .then((res) => {
        if (!alive) return;
        const list = res.data?.data?.plans || [];
        setPlans(list);
        //預設選第二個（通常是主推的組合方案），沒有就選第一個 ——
        //讓底部的結帳鈕一進來就有金額，不要是一顆按不下去的灰鈕
        setSelectedId((list[1] || list[0])?.id || null);
      })
      .catch(
        (e) => alive && setError(e.response?.data?.message || "方案載入失敗"),
      )
      .finally(() => alive && setLoading(false));

    return () => {
      alive = false;
    };
  }, []);

  const selected = plans.find((p) => p.id === selectedId);

  //等券入帳。
  //  終止條件是「張數變多了」而不是「訂單 paid」：
  //  後端是先改單成 paid、才發券，中間那一瞬間看訂單狀態會以為好了，
  //  但券還沒進來 —— 這一頁要等的是券。
  const watchTickets = useCallback(() => {
    clearInterval(pollRef.current);
    const startedAt = Date.now();

    pollRef.current = setInterval(async () => {
      if (Date.now() - startedAt > POLL_MAX_MS) {
        clearInterval(pollRef.current);
        setSubmitting(false);
        return;
      }
      const t = await loadTickets();
      if (!t) return;
      if ((t[TICKET_TYPE] || 0) > beforeRef.current) {
        clearInterval(pollRef.current);
        setSubmitting(false);
        onBought?.();
      }
    }, POLL_MS);
  }, [loadTickets, onBought]);

  //按下結帳鈕。
  //
  //  **登入要擋在這裡，不是擋在發票填完之後** ——
  //  券是 $inc 到使用者帳號上的，沒有帳號就沒有地方可以發。
  //  讓他把載具、統編都填完才說「請先登入」是很糟的體驗，
  //  而那正是這道檢查原本放錯位置時會發生的事。
  const handleCheckoutClick = () => {
    if (!isLoggedIn) {
      requireLogin(null, "請先登入，以保存您的問卦券。");
      return;
    }
    setInvoiceOpen(true);
  };

  const handleCheckout = async (invoice) => {
    if (!selected || submitting) return;

    //  保險：正常情況走不到這裡（按結帳時就擋掉了），但登入狀態
    //  可能在填發票的途中失效（token 過期、在別的分頁登出）。
    if (!isLoggedIn) {
      setInvoiceOpen(false);
      requireLogin(null, "請先登入，以保存您的問卦券。");
      return;
    }
    setSubmitting(true);
    setError("");
    beforeRef.current = tickets?.[TICKET_TYPE] || 0;

    //—— 分頁要在這裡開 ——
    //  這一行還在使用者按下按鈕的那個事件裡。等建單的 API 回來才開，
    //  瀏覽器會判定成「不是使用者要求的」而擋掉（理由見 utils/ecpay-redirect）。
    //
    //  開新分頁而不是整頁跳轉：這一頁留著，使用者剛打的問題與問卦的進度
    //  才不會在付款途中被毀掉 —— 付完款切回來就能接著問。
    const win = openEcpayWindow();

    try {
      const res = await PaymentService.checkout(selected.id, invoice);
      if (!res.data?.status) {
        //發票欄位有問題時後端會回原因，要留在 sheet 上讓使用者改
        setError(res.data?.message || "建立訂單失敗");
        win?.close();
        setSubmitting(false);
        return;
      }

      const { action, params } = res.data.data;
      submitToEcpay(action, params, win);

      if (win) {
        //開在新分頁的話這一頁還在 —— 關掉發票 sheet、開始等券入帳。
        //  submitting 維持 true：這段期間不該讓他再買一次。
        setInvoiceOpen(false);
        watchTickets();
      }
      //被擋掉彈窗（win 是 null）時整頁已經跳走，這裡不用收尾。
    } catch (e) {
      setError(e.response?.data?.message || "建立訂單失敗，請稍後再試");
      win?.close();
      setSubmitting(false);
    }
  };

  return (
    <div className="fuyaozi-ticket">
      <header className="fuyaozi-ticket__head">
        <h2 className="fuyaozi-ticket__title">購買問卦券</h2>

        {tickets && (
          <span className="fuyaozi-ticket__balance">
            問卦券 <strong>{tickets.gua}</strong>
          </span>
        )}
      </header>

      {loading ? (
        <p className="fuyaozi-ticket__state">載入方案中…</p>
      ) : plans.length === 0 ? (
        <p className="fuyaozi-ticket__state">{error || "目前沒有可購買的方案"}</p>
      ) : (
        <ul className="fuyaozi-ticket__plans">
          {plans.map((plan, i) => {
            const active = plan.id === selectedId;
            return (
              <li key={plan.id}>
                <button
                  type="button"
                  className={active ? "fuyaozi-ticket__plan is-active" : "fuyaozi-ticket__plan"}
                  onClick={() => setSelectedId(plan.id)}
                  aria-pressed={active}
                >
                  {/* 折扣最高的方案標記為主推 */}
                  {plan.discount ===
                    Math.max(...plans.map((p) => p.discount || 0)) &&
                    plan.discount > 0 && (
                      <span className="fuyaozi-ticket__badge">BEST · 最划算</span>
                    )}

                  <div className="fuyaozi-ticket__plan-top">
                    <span className="fuyaozi-ticket__plan-name">{plan.name}</span>
                    <span className="fuyaozi-ticket__plan-sep">·</span>
                    <span className="fuyaozi-ticket__plan-summary">{plan.summary}</span>
                    <span className="fuyaozi-ticket__no">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>

                  {plan.perks && (
                    <ul className="fuyaozi-ticket__perks">
                      {plan.perks.map((perk) => (
                        <li key={perk}>
                          <svg
                            viewBox="0 0 24 24"
                            width="13"
                            height="13"
                            aria-hidden="true"
                          >
                            <path
                              d="M4 12.5l5 5L20 6.5"
                              stroke="currentColor"
                              strokeWidth="2.6"
                              fill="none"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          {perk}
                        </li>
                      ))}
                    </ul>
                  )}

                  <div className="fuyaozi-ticket__plan-bottom">
                    <span className="fuyaozi-ticket__unit">
                      {plan.discount && (
                        <b className="fuyaozi-ticket__discount">{plan.discount}% ↓</b>
                      )}
                      每次 NT${plan.perUnit}
                    </span>

                    <span className="fuyaozi-ticket__price">
                      <i>NT$</i>
                      {plan.price}
                    </span>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {/* 票券沒入帳的救生索。開 LINE 官方帳號而不是站內的客服頁 ——
          這一刻他已經付了錢卻沒拿到東西，要的是找得到人，
          不是再讀一頁說明。
          target="_blank"：原本這一頁正在輪詢等券入帳，
          被整頁帶走的話那個輪詢就斷了。 */}
      <a
        className="fuyaozi-ticket__help"
        href={LINE_URL}
        target="_blank"
        rel="noreferrer"
      >
        票券沒有儲值成功嗎？
      </a>

      {error && plans.length > 0 && <p className="fuyaozi-ticket__error">{error}</p>}

      {/* 結帳鈕固定在底部，金額跟著選擇走 */}
      <div className="fuyaozi-ticket__checkout">
        <button
          type="button"
          className="fuyaozi-ticket__checkout-btn"
          disabled={!selected || submitting}
          onClick={handleCheckoutClick}
        >
          {/* 綠界開在新分頁之後，這一頁是在「等券入帳」而不是「正在前往」——
              文案要講清楚，不然使用者會以為卡住了。 */}
          {submitting
            ? "在另一個分頁完成付款…"
            : `NT$${selected?.price ?? 0} 結帳`}
        </button>
      </div>

      <InvoiceSheet
        open={invoiceOpen}
        amount={selected?.price ?? 0}
        submitting={submitting}
        onClose={() => setInvoiceOpen(false)}
        onConfirm={handleCheckout}
      />
    </div>
  );
};

export default FuyaoziTicket;
